import {
  Building2,
  Check,
  Download,
  Plus,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useApp } from "../../domain/AppContext";
import type { UnitTrashItem } from "../../domain/types";
import { remoteRepository } from "../../services/remoteRepository";
import { Button, Dialog, Field, InlineMessage, PageHeader } from "../../components/ui";

interface LocalSettings {
  systemName: string;
  schoolYear: string;
  defaultUnit: string;
  guardianAuthorization: boolean;
  notifyDeadlines: boolean;
  weeklyDigest: boolean;
  sessionHours: string;
  vaultMinutes: string;
  mfa: boolean;
}

export function InstitutionPage() {
  const { profile, data, saveInstitution, notify } = useApp();
  const canEdit = Boolean(profile?.isSystemManager);
  const [tab, setTab] = useState<
    "general" | "address" | "units" | "owners" | "identity"
  >("general");
  const [form, setForm] = useState(data?.institution);
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitDraft, setUnitDraft] = useState({ name: '', code: '', address: '', owner: '' });
  if (!data || !form) return null;
  const save = async () => {
    const result = await saveInstitution(form);
    notify(
      result.ok
        ? { type: "success", title: "Dados institucionais salvos" }
        : { type: "error", title: result.error.message },
    );
  };
  return (
    <div className="institution-page">
      <PageHeader
        eyebrow="Configuração institucional"
        title="Instituição"
        description="Dados usados em inscrições, relatórios e certificados."
        actions={canEdit ? <Button onClick={save}>
            <Save size={17} /> Salvar dados
          </Button> : undefined}
      />
      <nav className="detail-tabs">
        {(["general", "address", "units", "owners", "identity"] as const).map(
          (item) => (
            <button
              type="button"
              key={item}
              className={tab === item ? "active" : ""}
              onClick={() => setTab(item)}
            >
              {item === "general"
                ? "Dados gerais"
                : item === "address"
                  ? "Endereço"
                  : item === "units"
                    ? "Unidades"
                    : item === "owners"
                      ? "Responsáveis"
                      : "Identidade"}
            </button>
          ),
        )}
      </nav>
      {!canEdit ? <InlineMessage title="Consulta somente leitura">A Coordenação pode visualizar os dados institucionais, mas somente o Gestor de Sistema pode alterá-los.</InlineMessage> : null}
      <fieldset className="institution-panel" disabled={!canEdit}>
        {tab === "general" ? (
          <>
            <h2>Dados gerais</h2>
            <div className="form-grid two">
              <Field label="Razão social">
                <input
                  value={form.legalName}
                  onChange={(event) =>
                    setForm({ ...form, legalName: event.target.value })
                  }
                />
              </Field>
              <Field label="Nome fantasia">
                <input
                  value={form.displayName}
                  onChange={(event) =>
                    setForm({ ...form, displayName: event.target.value })
                  }
                />
              </Field>
              <Field label="CNPJ">
                <input
                  value={form.cnpj}
                  onChange={(event) =>
                    setForm({ ...form, cnpj: event.target.value })
                  }
                />
              </Field>
              <Field label="Código INEP">
                <input
                  value={form.inep}
                  onChange={(event) =>
                    setForm({ ...form, inep: event.target.value })
                  }
                />
              </Field>
              <Field label="Telefone">
                <input
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                />
              </Field>
              <Field label="E-mail institucional">
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm({ ...form, email: event.target.value })
                  }
                />
              </Field>
            </div>
          </>
        ) : null}
        {tab === "address" ? (
          <>
            <h2>Endereço principal</h2>
            <div className="form-grid two">
              <Field label="Rua">
                <input value={form.address.street} onChange={(event) => setForm({ ...form, address: { ...form.address, street: event.target.value } })} />
              </Field>
              <Field label="Número">
                <input value={form.address.number} onChange={(event) => setForm({ ...form, address: { ...form.address, number: event.target.value } })} />
              </Field>
              <Field label="Complemento">
                <input value={form.address.complement} onChange={(event) => setForm({ ...form, address: { ...form.address, complement: event.target.value } })} />
              </Field>
              <Field label="Bairro">
                <input value={form.address.district} onChange={(event) => setForm({ ...form, address: { ...form.address, district: event.target.value } })} />
              </Field>
              <Field label="Cidade">
                <input value={form.address.city} onChange={(event) => setForm({ ...form, address: { ...form.address, city: event.target.value } })} />
              </Field>
              <Field label="CEP">
                <input value={form.address.postalCode} onChange={(event) => setForm({ ...form, address: { ...form.address, postalCode: event.target.value } })} />
              </Field>
            </div>
          </>
        ) : null}
        {tab === "units" ? (
          <>
            <div className="section-heading">
              <div>
                <h2>Unidades escolares</h2>
                <p>Escopo de dados, equipe e permissões.</p>
              </div>
              <Button
                variant="secondary"
                onClick={() => setUnitOpen(true)}
              >
                <Plus size={17} /> Adicionar unidade
              </Button>
            </div>
            <div className="responsive-table">
              <table>
                <thead>
                  <tr>
                    <th>Unidade</th>
                    <th>Código</th>
                    <th>Endereço</th>
                    <th>Responsável</th>
                    <th>Estudantes</th>
                  </tr>
                </thead>
                <tbody>{form.units.map((unit) => <tr key={unit.id}><td>{unit.name}</td><td>{unit.code}</td><td>{unit.address}</td><td>{unit.owner}</td><td>{unit.name === form.activeUnit ? data.students.length : '—'}</td></tr>)}</tbody>
              </table>
            </div>
          </>
        ) : null}
        {tab === "owners" ? (
          <>
            <h2>Responsáveis institucionais</h2>
            <div className="form-grid two">
              <Field label="Direção">
                <input value={form.owners.direction} onChange={(event) => setForm({ ...form, owners: { ...form.owners, direction: event.target.value } })} />
              </Field>
              <Field label="Coordenação pedagógica">
                <input value={form.owners.coordination} onChange={(event) => setForm({ ...form, owners: { ...form.owners, coordination: event.target.value } })} />
              </Field>
              <Field label="Olimpíadas">
                <input value={form.owners.olympiads} onChange={(event) => setForm({ ...form, owners: { ...form.owners, olympiads: event.target.value } })} />
              </Field>
            </div>
          </>
        ) : null}
        {tab === "identity" ? (
          <>
            <h2>Identidade da instituição</h2>
            <div className="identity-preview">
              <img
                src={form.logoDataUrl || "./brand/oficina-sapiens-logo-horizontal-univap.png"}
                alt="Logo atual dos Colégios Univap"
              />
              <div>
                <strong>Logo da escola</strong>
                <p>
                  Acompanha o ícone do Oficina Sapiens em certificados e
                  relatórios.
                </p>
                <label className="button button-secondary">Substituir logo<input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => { const file = event.target.files?.[0]; if (!file) return; if (file.size > 1024 * 1024) return notify({ type: 'error', title: 'A logo deve ter até 1 MB' }); const reader = new FileReader(); reader.onload = () => setForm({ ...form, logoDataUrl: String(reader.result) }); reader.readAsDataURL(file); }} /></label>
              </div>
            </div>
            <InlineMessage title="Identidade protegida">
              As cores institucionais do Oficina Sapiens não são editáveis no
              MVP.
            </InlineMessage>
          </>
        ) : null}
      </fieldset>
      <Dialog open={unitOpen} title="Adicionar unidade" description="A unidade será incluída no cadastro institucional local ao salvar os dados." onClose={() => setUnitOpen(false)} actions={<><Button variant="ghost" onClick={() => setUnitOpen(false)}>Cancelar</Button><Button onClick={() => { if (!unitDraft.name.trim() || !unitDraft.code.trim()) return notify({ type: 'error', title: 'Informe nome e código da unidade' }); setForm({ ...form, units: [...form.units, { ...unitDraft, id: crypto.randomUUID() }] }); setUnitDraft({ name: '', code: '', address: '', owner: '' }); setUnitOpen(false); }}>Adicionar unidade</Button></>}><div className="dialog-form"><Field label="Nome"><input value={unitDraft.name} onChange={(event) => setUnitDraft({ ...unitDraft, name: event.target.value })} /></Field><div className="form-grid two"><Field label="Código"><input value={unitDraft.code} onChange={(event) => setUnitDraft({ ...unitDraft, code: event.target.value })} /></Field><Field label="Responsável"><input value={unitDraft.owner} onChange={(event) => setUnitDraft({ ...unitDraft, owner: event.target.value })} /></Field></div><Field label="Endereço"><input value={unitDraft.address} onChange={(event) => setUnitDraft({ ...unitDraft, address: event.target.value })} /></Field></div></Dialog>
    </div>
  );
}

export function SettingsPage() {
  const { profile, data, notify, refresh } = useApp();
  const [trashItems, setTrashItems] = useState<UnitTrashItem[]>([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUnitId, setResetUnitId] = useState(profile?.unitId ?? '');
  const [resetPassword, setResetPassword] = useState('');
  const [resetWorking, setResetWorking] = useState(false);
  const [settings, setSettings] = useState<LocalSettings>(() => {
    const fallback: LocalSettings = {
      systemName: "Oficina Sapiens",
      schoolYear: String(new Date().getFullYear()),
      defaultUnit: "",
      guardianAuthorization: true,
      notifyDeadlines: true,
      weeklyDigest: true,
      sessionHours: "1",
      vaultMinutes: "15",
      mfa: false,
    };
    return fallback;
  });
  useEffect(() => {
    if (!profile?.isSystemManager) return;
    void remoteRepository.listUnitTrash().then((result) => { if (result.ok) setTrashItems(result.data); });
  }, [profile?.isSystemManager]);
  if (!data) return null;
  const toggle = (key: keyof typeof settings) =>
    typeof settings[key] === "boolean" &&
    setSettings((current) => ({ ...current, [key]: !current[key] }));
  const save = async () => {
    const result = await remoteRepository.saveApplicationRecord('system_setting', 'general', settings, profile?.unitId);
    notify(result.ok ? { type: "success", title: "Configurações salvas" } : { type: 'error', title: 'Configurações não salvas', description: result.error.message });
  };
  const exportData = () => {
    const safe = {
      olympiads: data.olympiads,
      enrollments: data.enrollments,
      students: data.students.map(
        ({ guardian: _guardian, birthDate: _birthDate, ...student }) => student,
      ),
      materials: data.materials,
      events: data.events,
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(safe, null, 2)], { type: "application/json" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "oficina-sapiens-dados.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const resetUnit = async () => {
    if (!resetUnitId || !resetPassword) return notify({ type: 'error', title: 'Selecione a unidade e confirme sua senha' });
    setResetWorking(true);
    const result = await remoteRepository.resetUnitData(resetUnitId, resetPassword);
    setResetWorking(false);
    if (!result.ok) return notify({ type: 'error', title: 'Dados não removidos', description: result.error.message });
    setResetOpen(false);
    setResetPassword('');
    await refresh();
    const trash = await remoteRepository.listUnitTrash();
    if (trash.ok) setTrashItems(trash.data);
    notify({ type: 'success', title: 'Dados enviados para a lixeira', description: 'A restauração ficará disponível por 60 dias.' });
  };
  const restoreUnit = async (item: UnitTrashItem) => {
    setResetWorking(true);
    const result = await remoteRepository.restoreUnitData(item.id);
    setResetWorking(false);
    if (!result.ok) return notify({ type: 'error', title: 'Dados não restaurados', description: result.error.message });
    await refresh();
    const trash = await remoteRepository.listUnitTrash();
    if (trash.ok) setTrashItems(trash.data);
    notify({ type: 'success', title: `Dados de ${item.unitName} restaurados` });
  };
  return (
    <div className="settings-page">
      <PageHeader
        eyebrow="Regras globais"
        title="Configurações"
        description="Preferências para o funcionamento da plataforma."
        actions={
          <Button onClick={save}>
            <Save size={17} /> Salvar
          </Button>
        }
      />
      <div className="settings-layout">
        <section>
          <h2>Geral</h2>
          <div className="form-grid two">
            <Field label="Nome do sistema">
              <input value={settings.systemName} onChange={(event) => setSettings({ ...settings, systemName: event.target.value })} />
            </Field>
            <Field label="Ano letivo ativo">
              <select value={settings.schoolYear} onChange={(event) => setSettings({ ...settings, schoolYear: event.target.value })}>
                <option>2026</option>
                <option>2027</option>
              </select>
            </Field>
            <Field label="Unidade padrão">
              <select value={settings.defaultUnit} onChange={(event) => setSettings({ ...settings, defaultUnit: event.target.value })}>
                {data.institution.units.map((unit) => <option key={unit.id} value={unit.name}>{unit.name}</option>)}
              </select>
            </Field>
          </div>
        </section>
        <section>
          <h2>Inscrições</h2>
          <InlineMessage title="Confirmação coerente com a cobrança">Inscrições gratuitas são confirmadas automaticamente. Inscrições pagas só são confirmadas após o registro de todas as cobranças exigíveis.</InlineMessage>
          <SettingToggle
            title="Exigir autorização do responsável"
            description="Para estudantes menores de 16 anos."
            checked={settings.guardianAuthorization}
            onChange={() => toggle("guardianAuthorization")}
          />
        </section>
        <section>
          <h2>Segurança</h2>
          <div className="form-grid two">
            <Field label="Inatividade da sessão">
              <select
                value={settings.sessionHours}
                onChange={(event) =>
                  setSettings({ ...settings, sessionHours: event.target.value })
                }
              >
                <option value="1">1 hora</option>
              </select>
            </Field>
            <Field label="Exibição de credenciais">
              <select
                value={settings.vaultMinutes}
                onChange={(event) =>
                  setSettings({ ...settings, vaultMinutes: event.target.value })
                }
              >
                <option value="15">15 minutos</option>
                <option value="30">30 minutos</option>
              </select>
            </Field>
          </div>
          <SettingToggle
            title="Segundo fator para o Gestor de Sistema"
            description="Disponível após a configuração institucional do segundo fator de acesso."
            checked={false}
            onChange={() => undefined}
            disabled
          />
        </section>
        <section>
          <h2>Notificações e dados</h2>
          <SettingToggle
            title="Avisar estudantes sobre novos prazos"
            description="Notificação na plataforma e por e-mail quando configurado."
            checked={settings.notifyDeadlines}
            onChange={() => toggle("notifyDeadlines")}
          />
          <SettingToggle
            title="Resumo semanal para professores"
            description="Consolida pendências e próximos eventos."
            checked={settings.weeklyDigest}
            onChange={() => toggle("weeklyDigest")}
          />
          <div className="data-actions">
            <Button variant="secondary" onClick={exportData}>
              <Download size={17} /> Exportar dados
            </Button>
          </div>
        </section>
        {profile?.isSystemManager ? <section className="academic-reset-section">
          <h2>Gestão do ano letivo</h2>
          <InlineMessage type="error" title="Limpeza por unidade">Remove estudantes, turmas identificadas nos cadastros, olimpíadas e registros acadêmicos vinculados somente da unidade escolhida. Professores, outras unidades e configurações institucionais são preservados.</InlineMessage>
          <div className="data-actions"><Button variant="danger" onClick={() => { setResetUnitId(profile.unitId); setResetOpen(true); }}><Trash2 size={17} /> Resetar dados de uma unidade</Button></div>
          {trashItems.filter((item) => !item.restoredAt).length ? <div className="trash-list"><h3>Lixeira</h3>{trashItems.filter((item) => !item.restoredAt).map((item) => <article key={item.id}><div><strong>{item.unitName}</strong><span>{item.counts.students ?? 0} estudantes · {item.counts.classes ?? 0} turmas · {item.counts.olympiads ?? 0} olimpíadas</span><small>Disponível até {new Date(item.expiresAt).toLocaleDateString('pt-BR')}</small></div><Button variant="secondary" loading={resetWorking} onClick={() => void restoreUnit(item)}><RotateCcw size={16} /> Restaurar</Button></article>)}</div> : null}
        </section> : null}
      </div>
      <Dialog open={resetOpen} title="Resetar dados da unidade" description="Esta ação move os dados para a lixeira por 60 dias. A unidade e os vínculos dos professores não serão excluídos." onClose={() => { if (!resetWorking) { setResetOpen(false); setResetPassword(''); } }} actions={<><Button variant="ghost" disabled={resetWorking} onClick={() => { setResetOpen(false); setResetPassword(''); }}>Cancelar</Button><Button variant="danger" loading={resetWorking} onClick={() => void resetUnit()}>Confirmar e mover para a lixeira</Button></>}><div className="dialog-form"><Field label="Unidade"><select value={resetUnitId} onChange={(event) => setResetUnitId(event.target.value)}>{data.institution.units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select></Field><Field label="Sua senha" hint="A senha do Gestor de Sistema é verificada antes da exclusão."><input type="password" autoComplete="current-password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} /></Field></div></Dialog>
    </div>
  );
}

function SettingToggle({
  title,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label className="setting-toggle">
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}
